import { Entity } from '@api/api/entity-schema';
import { ThemeExposure } from '@api/api/theme-overview';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { FollowThemeButton } from '@terminal/components/follow-theme-button/FollowThemeButton';
import { useThemeCompanies } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { ThemePerformanceSection } from '@terminal/views/theme-overview/theme-overview-drawer/components/theme-performance-section/ThemePerformanceSection';

import { TopCompaniesSection } from '../../theme-overview-content/components/top-companies-section/TopCompaniesSection';
import { StepperSection } from '../components/stepper-section/StepperSection';

import styles from './AoThemeOverviewTab.module.scss';

const TOP_COMPANIES_LIMIT = 10;

export interface AoThemeOverviewTabProps {
  entity: Entity;
  theme: ThemeExposure;
  title?: string;
  source: PosthogTrackingSource;
  tooltipThemeId?: string;
}

export const AoThemeOverviewTab = ({
  entity,
  theme,
  title,
  source,
  tooltipThemeId,
}: AoThemeOverviewTabProps) => {
  const {
    data: themeCompanies = [],
    isLoading,
    error,
  } = useThemeCompanies({
    themeId: theme.theme.id,
  });

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          { styles: { width: '100%', height: '700px', borderRadius: '8px' } },
        ]}
      />
    );
  }

  if (error) {
    throw error;
  }

  const entityTags = themeCompanies
    .map(item => item.company)
    .slice(0, TOP_COMPANIES_LIMIT);
  const companies = themeCompanies.slice(0, TOP_COMPANIES_LIMIT);
  const remainingCount = Math.max(
    0,
    themeCompanies.length - TOP_COMPANIES_LIMIT
  );

  return (
    <div className={styles.root}>
      <StepperSection
        entity={entity}
        theme={theme}
        title={title}
        activeIndex={1}
        description={theme.theme.description}
        rightHeaderNode={<FollowThemeButton themeId={theme.theme.id} />}
      />
      <ErrorBoundaryWrapper>
        <ThemePerformanceSection
          themeId={theme.theme.id}
          entityTags={entityTags}
        />
      </ErrorBoundaryWrapper>
      {!!companies.length && (
        <TopCompaniesSection
          companies={companies}
          remainingCount={remainingCount}
          source={source}
          tooltipThemeId={tooltipThemeId}
        />
      )}
    </div>
  );
};
